"use client";
import React, { useCallback, useEffect, useState } from "react";
import { TeamCard } from "./TeamCard";
import { GroupHeader } from "../../../ui/Headers";
import { useCompanyStaff } from "src/app/lib/useCompanyStaff";
import { useDirectors } from "src/app/lib/useCompanyDirector";
import { MemberType } from "src/types";
import useEmblaCarousel from "embla-carousel-react";

const MeetTeam: React.FC = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: "auto",
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const { data } = useCompanyStaff();
  const { data: directorsData } = useDirectors();
  const isDemoMode = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  const staff: MemberType[] = [
    ...(!isDemoMode ? (directorsData as MemberType[]) || [] : []),
    ...((data?.staff as MemberType[]) || []),
  ];

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const updateButtons = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    updateButtons();
    emblaApi.on("select", updateButtons);
    emblaApi.on("reInit", updateButtons);
  }, [emblaApi, updateButtons]);

  if (!staff || staff.length === 0) {
    return (
      <section className="py-20">
        <div className="wrapper">
          <div className="item mb-4">
            <GroupHeader
              max={1024}
              subheader="Meet our dedicated team, committed to delivering exceptional real estate service."
              header="Meet Our Team"
            />
          </div>
          <div className="text-center py-12">
            <p className="text-gray-500">No team members available</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20">
      <div className="wrapper">
        <div className="item mb-4">
          <GroupHeader
            max={1024}
            subheader="Meet our dedicated team, committed to delivering exceptional real estate service."
            header="Meet Our Team"
          />
        </div>

        <div className="pt-14">
          {staff.length === 1 ? (
            // Single card
            <div className="flex justify-center">
              <div className="w-full max-w-[350px]">
                <TeamCard className="w-full h-full" {...staff[0]} />
              </div>
            </div>
          ) : (
            // Carousel
            <div className="relative">
              {/* Prev button */}
              <button
                className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 transition-opacity ${canScrollPrev
                    ? "opacity-100"
                    : "opacity-0 pointer-events-none"
                  }`}
                onClick={scrollPrev}
              >
                {/* svg left arrow */}
                <svg width="23" height="40" viewBox="0 0 23 40" fill="none">
                  <path
                    d="M22.1211 39.092C22.6836 38.5294 22.9995 37.7665 22.9995 36.971C22.9995 36.1755 22.6836 35.4126 22.1211 34.85L7.27114 20L22.1211 5.14998C22.6676 4.58417 22.97 3.82636 22.9632 3.03977C22.9563 2.25318 22.6408 1.50074 22.0846 0.944519C21.5284 0.388294 20.7759 0.0727921 19.9893 0.0659561C19.2028 0.0591202 18.445 0.361504 17.8791 0.907978L0.908144 17.879C0.345731 18.4416 0.0297852 19.2045 0.0297852 20C0.0297852 20.7955 0.345731 21.5584 0.908144 22.121L17.8791 39.092C18.4417 39.6544 19.2047 39.9703 20.0001 39.9703C20.7956 39.9703 21.5586 39.6544 22.1211 39.092Z"
                    fill="var(--primary-color)"
                  />
                </svg>
              </button>

              {/* Embla wrapper */}
              <div
                className="overflow-x-hidden pt-24 max-w-[900px] mx-auto"
                ref={emblaRef}
              >
                <div className="flex w-full ">
                  {staff.map((member, index) => (
                    <div
                      key={index}
                      className="shrink-0 sm:w-full sm:grid sm:place-items-center sm:pr-0 pr-10 w-1/2"
                    >
                      <TeamCard
                        className="w-full sm:max-w-[85%] h-full"
                        {...member}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Next button */}
              <button
                className={`absolute right-4 top-1/2 -translate-y-1/2 z-10 transition-opacity ${canScrollNext
                    ? "opacity-100"
                    : "opacity-0 pointer-events-none"
                  }`}
                onClick={scrollNext}
              >
                {/* svg right arrow */}
                <svg width="23" height="40" viewBox="0 0 23 40" fill="none">
                  <path
                    d="M0.879103 39.0919C0.316689 38.5293 0.000741959 37.7663 0.000741959 36.9709C0.000741959 36.1754 0.316689 35.4124 0.879103 34.8499L15.7291 19.9999L0.879103 5.14986C0.332628 4.58405 0.0302448 3.82624 0.0370808 3.03965C0.0439148 2.25306 0.359421 1.50062 0.915646 0.944397C1.47187 0.388172 2.22431 0.07267 3.0109 0.065834C3.79749 0.0589981 4.55529 0.361382 5.1211 0.907856L22.0921 17.8789C22.6545 18.4414 22.9705 19.2044 22.9705 19.9999C22.9705 20.7953 22.6545 21.5583 22.0921 22.1209L5.1211 39.0919C4.55852 39.6543 3.79559 39.9702 3.0001 39.9702C2.20461 39.9702 1.44168 39.6543 0.879103 39.0919Z"
                    fill="var(--primary-color)"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MeetTeam;
